import { buildEntityPath, buildProjectSnapshotPath, normalizeProjectPath, PROJECT_LEVEL_ROUTE_PATHS, splitPath, } from "./build.js";
import { buildEntityTabPath, ENTITY_TAB_ROUTE_SEGMENT, parseEntityTabSuffix, } from "./tab-intents.js";
import { canonicalizeEntityVersionIntent, composeEntityVersionPath, parseEntityVersionSuffix, } from "./version-intents.js";
function buildAliasToTypeMap(aliases) {
    const aliasToType = new Map();
    for (const [entityTypeKey, alias] of Object.entries(aliases.aliasesByEntityType ?? {})) {
        if (alias) {
            aliasToType.set(alias, entityTypeKey);
        }
    }
    return aliasToType;
}
export function findRootFolder(structure, rootFolderSlug) {
    return (structure?.rootFolders ?? []).find((rootFolder) => {
        const candidateSlug = rootFolder.slug || rootFolder.key;
        return candidateSlug === rootFolderSlug || rootFolder.key === rootFolderSlug;
    });
}
export function isProjectTreeFolderRoot(input) {
    return (input.structure?.folderNodes ?? []).some((node) => node.kind === "root" &&
        node.rootFolderKey === input.rootFolderKey &&
        node.settings?.projectTreeFolders?.enabled === true);
}
function resolveCompiledFolderRoute(input) {
    const rootFolder = findRootFolder(input.structure, input.rootFolderSlug);
    if (!rootFolder) {
        return null;
    }
    if (input.segments.length === 0) {
        return {
            folderPath: "",
        };
    }
    if (isProjectTreeFolderRoot({ structure: input.structure, rootFolderKey: rootFolder.key })) {
        return {
            folderPath: input.segments.join("/"),
            folderSlug: input.segments[0],
            ...(input.segments[1] ? { collectionSlug: input.segments[1] } : {}),
        };
    }
    const folderNodes = input.structure?.folderNodes ?? [];
    const rootNode = folderNodes.find((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key);
    if (!rootNode) {
        return null;
    }
    let parentKey = rootNode.key;
    let matchedNode = null;
    for (let index = 0; index < input.segments.length; index += 1) {
        const segment = input.segments[index];
        const childNode = folderNodes.find((node) => node.parentKey === parentKey &&
            node.slug === segment &&
            (node.kind === "folder" || node.kind === "collection")) ?? null;
        if (!childNode) {
            return null;
        }
        matchedNode = childNode;
        if (childNode.kind === "collection") {
            if (index !== input.segments.length - 1) {
                return null;
            }
            return {
                folderPath: input.segments.join("/"),
                folderSlug: input.segments[0],
                collectionSlug: childNode.slug,
            };
        }
        parentKey = childNode.key;
    }
    if (!matchedNode || matchedNode.kind !== "folder") {
        return null;
    }
    return {
        folderPath: input.segments.join("/"),
        folderSlug: input.segments[0],
    };
}
function parseGenericEntityRouteBase(input) {
    if (!input.aliases) {
        return null;
    }
    const aliasToType = buildAliasToTypeMap(input.aliases);
    const folderSlugs = [];
    const entitySegments = [];
    let encounteredAlias = false;
    for (const segment of input.trailingSegments) {
        if (!encounteredAlias && !aliasToType.has(segment)) {
            folderSlugs.push(segment);
            continue;
        }
        encounteredAlias = true;
        entitySegments.push(segment);
    }
    if (!encounteredAlias) {
        return null;
    }
    if (entitySegments.length < 2 || entitySegments.length % 2 !== 0) {
        return null;
    }
    const entityLineage = [];
    for (let index = 0; index < entitySegments.length - 2; index += 2) {
        const entityAlias = entitySegments[index];
        const entitySlug = entitySegments[index + 1];
        const entityTypeKey = aliasToType.get(entityAlias);
        if (!entityTypeKey) {
            return null;
        }
        entityLineage.push({ entityTypeKey, entityAlias, entitySlug });
    }
    const entityAlias = entitySegments[entitySegments.length - 2];
    const entitySlug = entitySegments[entitySegments.length - 1];
    const entityTypeKey = aliasToType.get(entityAlias);
    if (!entityTypeKey) {
        return null;
    }
    const canonicalPath = buildEntityPath({
        kind: "entity",
        entityTypeKey,
        entityAlias,
        entitySlug,
        scope: {
            rootFolderSlug: input.rootFolderSlug,
            folderSlugs,
            entityLineage,
        },
    });
    return {
        routeKey: "entity",
        params: {
            rootFolderSlug: input.rootFolderSlug,
            folderPath: folderSlugs.join("/"),
            entityTypeKey,
            entityAlias,
            entitySlug,
        },
        normalizedPath: input.normalizedPath,
        canonicalPath,
        baseEntityPath: canonicalPath,
        entityRouteLocation: {
            baseEntityPath: canonicalPath,
            intent: { kind: "current" },
            path: canonicalPath,
        },
    };
}
function parseGenericEntityRoute(input) {
    const fullRoute = parseGenericEntityRouteBase(input);
    if (fullRoute) {
        return fullRoute;
    }
    const versionSuffix = parseEntityVersionSuffix(input.trailingSegments);
    const afterVersion = versionSuffix
        ? input.trailingSegments.slice(0, versionSuffix.baseSegmentCount)
        : input.trailingSegments;
    if (versionSuffix) {
        const versionBaseRoute = parseGenericEntityRouteBase({
            ...input,
            trailingSegments: afterVersion,
            normalizedPath: normalizeProjectPath(`/${input.rootFolderSlug}/${afterVersion.join("/")}`),
        });
        if (versionBaseRoute) {
            const versionIntent = canonicalizeEntityVersionIntent(versionSuffix.intent);
            if (!versionIntent) {
                return null;
            }
            const canonicalPath = composeEntityVersionPath(versionBaseRoute.canonicalPath, versionIntent);
            return {
                ...versionBaseRoute,
                normalizedPath: input.normalizedPath,
                canonicalPath,
                versionIntent,
                baseEntityPath: versionBaseRoute.canonicalPath,
                entityRouteLocation: {
                    baseEntityPath: versionBaseRoute.canonicalPath,
                    intent: versionIntent,
                    path: canonicalPath,
                },
            };
        }
    }
    const tabSuffix = parseEntityTabSuffix(afterVersion);
    const bareTabSuffix = tabSuffix === null && afterVersion[afterVersion.length - 1] === ENTITY_TAB_ROUTE_SEGMENT
        ? { baseSegmentCount: afterVersion.length - 1, tabSlug: "" }
        : null;
    const effectiveTabSuffix = tabSuffix ?? bareTabSuffix;
    const afterTab = effectiveTabSuffix
        ? afterVersion.slice(0, effectiveTabSuffix.baseSegmentCount)
        : afterVersion;
    if (!versionSuffix && !effectiveTabSuffix) {
        return null;
    }
    const baseRoute = parseGenericEntityRouteBase({
        ...input,
        trailingSegments: afterTab,
        normalizedPath: normalizeProjectPath(`/${input.rootFolderSlug}/${afterTab.join("/")}`),
    });
    if (!baseRoute) {
        return null;
    }
    const versionIntent = versionSuffix
        ? canonicalizeEntityVersionIntent(versionSuffix.intent)
        : undefined;
    const tabPath = tabSuffix
        ? buildEntityTabPath(baseRoute.canonicalPath, tabSuffix.tabSlug)
        : baseRoute.canonicalPath;
    const canonicalPath = versionIntent ? composeEntityVersionPath(tabPath, versionIntent) : tabPath;
    return {
        ...baseRoute,
        normalizedPath: input.normalizedPath,
        canonicalPath,
        ...(versionIntent ? { versionIntent } : {}),
        ...(effectiveTabSuffix ? { entityTabSlug: effectiveTabSuffix.tabSlug } : {}),
        baseEntityPath: baseRoute.canonicalPath,
        entityRouteLocation: {
            baseEntityPath: baseRoute.canonicalPath,
            intent: versionIntent ?? { kind: "current" },
            path: canonicalPath,
        },
    };
}
export function parseProjectPath(path, options) {
    const normalizedPath = normalizeProjectPath(path);
    if (normalizedPath === "/") {
        return {
            routeKey: "welcome",
            params: {},
            normalizedPath,
            canonicalPath: "/",
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.projectDocuments) {
        return {
            routeKey: "projectDocuments",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.projectDocuments,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.aiAssistant) {
        return {
            routeKey: "aiAssistant",
            params: {},
            normalizedPath,
            canonicalPath: normalizedPath,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.aiAssistantNewChat) {
        return {
            routeKey: "aiAssistantNewChat",
            params: {},
            normalizedPath,
            canonicalPath: normalizedPath,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.aiAssistantHistory) {
        return {
            routeKey: "aiAssistantHistory",
            params: {},
            normalizedPath,
            canonicalPath: normalizedPath,
        };
    }
    if (normalizedPath.startsWith("/ai-assistant/thread/")) {
        const segments = splitPath(normalizedPath);
        if (segments.length === 3 && segments[0] === "ai-assistant" && segments[1] === "thread") {
            return {
                routeKey: "aiAssistantThread",
                params: {
                    threadId: decodeURIComponent(segments[2]),
                },
                normalizedPath,
                canonicalPath: normalizedPath,
            };
        }
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.specificationClarificationRequests) {
        return {
            routeKey: "specificationClarificationRequests",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.specificationClarificationRequests,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.knowledge) {
        return {
            routeKey: "knowledge",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.knowledge,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots) {
        return {
            routeKey: "specificationVersionSnapshots",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots,
        };
    }
    if (normalizedPath.startsWith(`${PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots}/`)) {
        const segments = splitPath(normalizedPath);
        if (segments.length === 2) {
            const snapshotReleaseNumber = decodeURIComponent(segments[1]);
            if (/^\d+$/.test(snapshotReleaseNumber)) {
                return {
                    routeKey: "specificationVersionSnapshots",
                    params: { snapshotReleaseNumber },
                    normalizedPath,
                    canonicalPath: buildProjectSnapshotPath(snapshotReleaseNumber),
                };
            }
        }
        return {
            routeKey: "specificationVersionSnapshots",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.projectQualityGuardConfiguration) {
        return {
            routeKey: "projectQualityGuardConfiguration",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.projectQualityGuardConfiguration,
        };
    }
    if (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.specificationQualityGuard) {
        return {
            routeKey: "specificationQualityGuard",
            params: {},
            normalizedPath,
            canonicalPath: PROJECT_LEVEL_ROUTE_PATHS.specificationQualityGuard,
        };
    }
    const segments = splitPath(normalizedPath);
    if (segments.length === 1) {
        const rootFolderSlug = decodeURIComponent(segments[0]);
        if (options.structure && !findRootFolder(options.structure, rootFolderSlug)) {
            return {
                routeKey: "notFound",
                params: { path: normalizedPath },
                normalizedPath,
                canonicalPath: normalizedPath,
            };
        }
        return {
            routeKey: "folder",
            params: {
                rootFolderSlug,
                folderPath: "",
            },
            normalizedPath,
            canonicalPath: normalizedPath,
        };
    }
    if (segments.length < 2) {
        return {
            routeKey: "notFound",
            params: { path: normalizedPath },
            normalizedPath,
            canonicalPath: normalizedPath,
        };
    }
    const rootFolderSlug = decodeURIComponent(segments[0]);
    const head = decodeURIComponent(segments[1]);
    const rest = segments.slice(2).map((segment) => decodeURIComponent(segment));
    const genericEntityRoute = parseGenericEntityRoute({
        rootFolderSlug,
        trailingSegments: [head, ...rest],
        normalizedPath,
        aliases: options.aliases,
    });
    if (genericEntityRoute) {
        return genericEntityRoute;
    }
    const folderRoute = resolveCompiledFolderRoute({
        structure: options.structure,
        rootFolderSlug,
        segments: [head, ...rest],
    });
    if (options.structure && !folderRoute) {
        return {
            routeKey: "notFound",
            params: { path: normalizedPath },
            normalizedPath,
            canonicalPath: normalizedPath,
        };
    }
    return {
        routeKey: "folder",
        params: {
            rootFolderSlug,
            folderPath: folderRoute?.folderPath ?? [head, ...rest].join("/"),
            ...(folderRoute?.folderSlug ? { folderSlug: folderRoute.folderSlug } : {}),
            ...(folderRoute?.collectionSlug ? { collectionSlug: folderRoute.collectionSlug } : {}),
        },
        normalizedPath,
        canonicalPath: normalizedPath,
    };
}
export function entityTypeKeyFromAlias(aliases, alias) {
    return buildAliasToTypeMap(aliases).get(alias) ?? null;
}
